import { z } from "zod";

/**
 * Building-block schemas shared between `artifacts.ts` (the typed artifact
 * response shapes) and `artifacts-view.ts` (the schema-driven view payload).
 * Lives in its own file so the view module can reference these without
 * importing back into `artifacts.ts`, which would create a cycle and force
 * the `view` field on `heavyArtifact` to fall back to a `z.custom<T>()`
 * brand instead of the real schema.
 *
 * Both `artifacts.ts` and `artifacts-view.ts` import from here. `artifacts.ts`
 * also re-exports each schema and its inferred type so existing call sites
 * keep working unchanged.
 */

/**
 * Whether this status puts a version beyond action.
 *
 * `"cancelled"` is the user calling it off. `"updated"` is a newer version
 * having replaced it. The words differ on the card because what happened
 * differs — nobody cancelled the draft they rewrote — but to a send path, an
 * approval guard, or a "still live" query they mean the same thing: this is not
 * the row to act on.
 *
 * Reach for this anywhere a check reads `status === "cancelled"` to mean "dead".
 * Before `"updated"` existed, supersession was written as `"cancelled"` and
 * every one of those guards covered it by accident; splitting the statuses is
 * what makes the second half something you have to say out loud.
 */
export function isCancelledOrSuperseded(status: string | undefined): boolean {
  return status === "cancelled" || status === "updated";
}

/**
 * Whether a cancellation only takes the user's own calendar copy, leaving the
 * meeting standing for everyone else.
 *
 * `canModifyForEveryone` is the signal, not `isOrganizer`: an event on a shared
 * calendar the user writes to, or one whose organizer allowed guests to modify
 * it, carries `isOrganizer: false` and still deletes for everyone. Branching on
 * the role there would tell the user nothing propagated while the meeting was
 * being destroyed for every guest (BIG-5314).
 *
 * `undefined` — a change saved before the field was persisted — reads as "can
 * reach everyone", so an unknown change keeps the cancellation wording rather
 * than claiming a containment nobody established.
 *
 * Lives here because the approval card, the send confirmation, and the agent's
 * terminal message all have to agree, and they sit in three different layers.
 */
export function isAttendeeSelfRemoval(change: {
  isCancellation?: boolean;
  canModifyForEveryone?: boolean;
}): boolean {
  return change.isCancellation === true && change.canModifyForEveryone === false;
}

export const emailDraftParticipant = z.object({
  id: z.string(),
  urn: z.string(),
  name: z.string(),
  picture: z.string().optional(),
  value: z.string(),
  type: z.enum(["emailAddress", "phoneNumber", "address"]),
  contactId: z.string(),
  isDomainHandle: z.boolean().optional(),
});
export type EmailDraftParticipant = z.infer<typeof emailDraftParticipant>;

// Fields shared by every draft-attachment variant. `filename`, `contentType`,
// and `size` are denormalized onto the draft so the send path and the UI never
// have to re-resolve the source artifact (which may have been garbage-collected
// since it was attached).
const draftAttachmentCommon = {
  filename: z.string(),
  contentType: z.string(),
  // Byte length of the original file. Used for the total-size guard at send and
  // for the size label in the UI.
  size: z.number(),
};

/**
 * A file attached to an outbound email draft, resolved to raw bytes at send time
 * (see `resolveDraftAttachments`). Both supported variants are the user's OWN
 * data being forwarded outbound, so neither needs a permission check:
 *
 *  - `gcsFile`: an uploaded file, or an inbound email attachment already
 *    extracted to GCS, addressed by its GCS object id (`fileId`).
 *  - `emailAttachment`: a raw inbound attachment row whose bytes live base64 in
 *    Postgres, addressed by `attachments.id`.
 *
 * Artifact types without retrievable file bytes (generated `document` content,
 * applets, invitations, contacts, calendar events) are intentionally NOT
 * attachable here — sharing those is gated on a future permission system.
 */
export const draftAttachmentSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("gcsFile"), fileId: z.string(), ...draftAttachmentCommon }),
  z.object({
    kind: z.literal("emailAttachment"),
    attachmentId: z.number(),
    ...draftAttachmentCommon,
  }),
]);
export type DraftAttachment = z.infer<typeof draftAttachmentSchema>;

/**
 * Stable client-facing id for a draft attachment, used both when rendering the
 * chip (`emailAttachmentView.id`) and when the client passes it back to DELETE.
 * `gcsFile` uses its GCS object id directly; `emailAttachment` is namespaced
 * (`email:<id>`) so the two id spaces can't collide. The DELETE handler and the
 * view builder MUST derive the id the same way, so they share this helper.
 */
export function draftAttachmentViewId(a: DraftAttachment): string {
  return a.kind === "gcsFile" ? a.fileId : `email:${a.attachmentId}`;
}

export const schedulingKickoffParticipant = emailDraftParticipant.extend({
  optional: z.boolean().default(false),
});
export type SchedulingKickoffParticipant = z.infer<typeof schedulingKickoffParticipant>;

export const eventParticipant = z.object({
  id: z.string(),
  contactId: z.string(),
  urn: z.string(),
  name: z.string(),
  picture: z.string().optional(),
  state: z.enum(["attending", "tentative", "declined", "unknown"]),
  value: z.string(),
  type: z.enum(["emailAddress", "phoneNumber", "address"]),
  /**
   * This address belongs to the user's account.
   *
   * Account-wide on purpose: a second mailbox the user has connected is still
   * theirs, and an event confined to their own addresses reaches nobody else.
   * Decided when the participant is written and read back as stored, so a row
   * saved before this field existed carries no answer at all.
   *
   * Ask this, not `organizer`, on an outgoing invitation. One name covering
   * both meanings is what let "invite my work address" be read as "my work
   * address is hosting" (BIG-7209).
   */
  isSelf: z.boolean().optional(),
  /**
   * The event's organizer, as the provider reported it.
   *
   * Only ever set on a **received** invitation, where a real third party
   * invited the user and the provider told us who. Absent on outgoing
   * invitations: the provider assigns the organizer from the calendar written
   * to, so we have nothing to say about it and nothing worth storing.
   */
  organizer: z.boolean().optional(),
  required: z.boolean().optional(),
  isDomainHandle: z.boolean().optional(),
  /** Mirrors `selfAdded` on the persisted participant — see ent/schema/artifacts.ts. */
  selfAdded: z.boolean().optional(),
});
export type EventParticipant = z.infer<typeof eventParticipant>;

export const virtualMeetingType = z.enum(["zoom", "teams", "googleMeet"]);

export const virtualMeeting = z.object({
  url: z.string().optional(),
  type: virtualMeetingType,
  phoneNumbers: z.array(z.string()).optional(),
});
export type VirtualMeeting = z.infer<typeof virtualMeeting>;

/**
 * Which product mark a connect card draws. A provider can front several
 * products — connecting Google as a calendar source should show the Calendar
 * mark, as a mailbox the Gmail mark, as a login the plain Google mark. The
 * three values name the three icon maps the clients keep
 * (`web/src/components/connectors/providerIcons.tsx`).
 *
 * Separate from `headerIcon` below: that enum is our own glyph set, while
 * these select among third-party brand marks the client holds as SVGs.
 */
export const connectorIconVariant = z.enum(["brand", "calendar", "mail"]);
export type ConnectorIconVariant = z.infer<typeof connectorIconVariant>;

/**
 * Identifier for the icon a heavy artifact's chrome should display. Server
 * stamps this onto every heavy artifact response (sibling to
 * `artifactTypeTitle` and `statusLabel`); the client maps these names to
 * lucide icons (web) or SF Symbols (native). Lives at the response level
 * rather than inside `view.blocks` because chrome layout varies per surface
 * (inline card / fullscreen modal / mobile collapsed button / native bridge)
 * but the underlying data — which icon — does not.
 */
export const headerIcon = z.enum([
  // Chrome / artifact-type icons
  "calendar",
  "calendarCheck",
  "calendarPlus",
  "mail",
  "phone",
  "phoneCall",
  "document",
  "layoutDashboard",
  "reply",
  "messageSquareText",
  "messageSquare",
  "send",
  "pencil",
  "zap",
  "sparkle",
  "clock",
  "shield",
  "externalLink",
  // Status / outcome icons
  "check",
  "xCircle",
  "link",
  "alertTriangle",
  "info",
  "thumbsUp",
  "thumbsDown",
  "helpCircle",
  // Operation icons
  "archive",
  "mailOpen",
  "tag",
  "download",
  // Field icons (paired with `fieldRow` / `diff`)
  "mapPin",
  "video",
  "repeat",
  "bell",
  "arrowRight",
  "userPlus",
  "userMinus",
  "trash",
  // Transcript speaker icons
  "bot",
  "user",
]);
export type HeaderIcon = z.infer<typeof headerIcon>;

/**
 * Compact "biscuit" summary of a heavy artifact, surfaced when the artifact
 * is the most recent unresolved item in a thread and we want a small
 * call-to-action to overlay the omnibox. Sibling of `view` rather than a
 * derived form of it: the biscuit is its own surface with its own layout
 * (icon + subtitle header, a few labeled rows, an optional body excerpt,
 * plus the existing `actions[]`), not a compact rendering of view blocks.
 *
 * Server emits this only when `status === "waitingForUserAction"`. Clients
 * that don't handle the field render nothing — fully additive.
 */
export const artifactSummaryField = z.discriminatedUnion("type", [
  // Labeled value row — the bread-and-butter "When: ...", "Where: ..." shape.
  z.object({
    type: z.literal("value"),
    label: z.string().optional(),
    value: z.string(),
  }),
  // Diff row — old strikethrough, arrow, new bolded. Used by eventChange to
  // show e.g. time/location/title transitions on one line.
  z.object({
    type: z.literal("diff"),
    label: z.string().optional(),
    before: z.string(),
    after: z.string(),
  }),
  // Bulleted list with optional "+N more" tail. Used by emailChange to
  // preview the first few affected emails without overflowing the biscuit.
  z.object({
    type: z.literal("list"),
    label: z.string().optional(),
    items: z.array(z.string()),
    moreCount: z.number().int().nonnegative().optional(),
  }),
]);
export type ArtifactSummaryField = z.infer<typeof artifactSummaryField>;

export const artifactSummaryExcerpt = z.object({
  value: z.string(),
  /** When "html", the client renders DOMPurify-sanitized HTML (tight
   *  allowlist — paragraphs, line breaks, links, basic emphasis). When
   *  "plain", renders as plain text. Defaults to "plain". */
  format: z.enum(["plain", "html"]).default("plain"),
});
export type ArtifactSummaryExcerpt = z.infer<typeof artifactSummaryExcerpt>;

export const artifactSummary = z.object({
  /** Headline above the inner card, e.g. "Send a calendar invitation to participants". */
  title: z.string(),
  /** Bold first line of the inner card, e.g. the meeting title or email subject. */
  subtitle: z.string().optional(),
  /** Icon name paired with `subtitle` inside the inner card. Reuses the
   *  shared `headerIcon` registry the chrome icon uses, just at the inner
   *  card scope so the biscuit can show e.g. a `calendar` on an
   *  `invitation` content card while the outer chrome icon stays the same. */
  icon: headerIcon.optional(),
  /** Labeled rows in the inner card. Each row picks a `type` variant. */
  fields: z.array(artifactSummaryField).optional(),
  /** Body excerpt at the bottom of the inner card. */
  excerpt: artifactSummaryExcerpt.optional(),
  /** Visual tone for the inner card. "accent" tints the card (e.g. to flag an
   *  Instant Book kickoff). Generic + optional so any artifact can opt in;
   *  clients that don't handle it fall back to the default surface. */
  tone: z.enum(["accent"]).optional(),
});
export type ArtifactSummary = z.infer<typeof artifactSummary>;

/** A single event reminder. Matches Google's override shape so outbound sync is lossless;
 * Outlook-bound reminders are squashed to a single value by the outbound mapper.
 * Google's documented 40320-minute ceiling (4 weeks) is enforced at the Google
 * write boundary (`toGoogleRemindersBlock`), not here — Google's read API can
 * return larger values for auto-generated birthday reminders. */
export const reminderOverrideSchema = z.object({
  method: z.enum(["popup", "email"]),
  minutes: z.number().int().min(0),
});
export type ReminderOverride = z.infer<typeof reminderOverrideSchema>;

/**
 * An all-day event's covered days as floating calendar dates: `start` and `end`
 * are both inclusive `YYYY-MM-DD` strings with no time and no zone. A date
 * string has no frame to mistake, so unlike an epoch-ms pair it cannot be
 * zone-converted twice — this shape is the source of truth for an all-day
 * change, and the epoch-ms fields beside it are a derived UTC-midnight
 * projection kept for compatibility (BIG-6782).
 */
export const allDayRange = z.object({
  start: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  end: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});
export type AllDayRange = z.infer<typeof allDayRange>;

/**
 * Human-readable label for a single reminder, e.g. "30 mins before · Popup".
 * Shared by the backend event-change view/summary and the web thread card so
 * both surfaces render the same text for the same data.
 */
export function formatReminderOverride(reminder: ReminderOverride): string {
  const { minutes } = reminder;
  let lead: string;
  if (minutes === 0) {
    lead = "At start";
  } else if (minutes % 1440 === 0) {
    const days = minutes / 1440;
    lead = `${days} day${days === 1 ? "" : "s"} before`;
  } else if (minutes % 60 === 0) {
    const hours = minutes / 60;
    lead = `${hours} hr${hours === 1 ? "" : "s"} before`;
  } else {
    lead = `${minutes} min${minutes === 1 ? "" : "s"} before`;
  }
  const method = reminder.method === "email" ? "Email" : "Popup";
  return `${lead} · ${method}`;
}
